/**
 * Which column a board is ordered by, and which way.
 *
 * Shared by the two boards, which order rows in different places — the contacts
 * board asks the server, because it shows a page out of however many there are
 * and ordering the page would answer a question about the page; the segments
 * board orders in the browser, because the whole list already arrived. Neither
 * of those is visible here, and that is the point: this control chooses a
 * column and a direction, and the caller decides what that means.
 *
 * It therefore speaks in column ids rather than in either board's sort type.
 * The caller maps the id back to its own.
 */

export interface SortableColumn {
  readonly id: string
  readonly label: string
}

export function SortPicker({
  columns,
  chosen,
  direction,
  unsorted,
  onChange,
}: {
  /** Only the columns that can be ordered by. One that cannot says so by absence. */
  readonly columns: readonly SortableColumn[]
  /** The column in force, or undefined for the board's own order. */
  readonly chosen: string | undefined
  readonly direction: 'asc' | 'desc'
  /** What the board's own order is called, for the entry that clears this. */
  readonly unsorted: string
  readonly onChange: (column: string | undefined, direction: 'asc' | 'desc') => void
}) {
  return (
    <>
      <p className="mb-2 font-semibold text-[12px] text-t2 uppercase tracking-[0.06em]">Sort by</p>
      <select
        aria-label="Sort by"
        className="mb-2 w-full rounded-field border border-line bg-surface px-2 py-1.5 text-[13px] text-t1"
        onChange={(event) =>
          onChange(event.target.value === '' ? undefined : event.target.value, direction)
        }
        value={chosen ?? ''}
      >
        <option value="">{unsorted}</option>
        {columns.map((column) => (
          <option key={column.id} value={column.id}>
            {column.label}
          </option>
        ))}
      </select>

      <span className="flex gap-1">
        {(['asc', 'desc'] as const).map((each) => (
          <button
            className={`flex-1 rounded-field px-2 py-1.5 font-medium text-[12px] ${
              direction === each ? 'bg-brand-soft text-brand-strong' : 'text-t2 hover:bg-line-2'
            } disabled:opacity-50`}
            // Nothing to reverse while the board is in its own order.
            disabled={chosen === undefined}
            key={each}
            onClick={() => onChange(chosen, each)}
            type="button"
          >
            {each === 'asc' ? 'Ascending' : 'Descending'}
          </button>
        ))}
      </span>
    </>
  )
}
